import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function KirkleesCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Kirklees Pothole Claims | 29% U-Roads in RED | Fixtyer"
        description="Kirklees Council earns GREEN on spend but AMBER overall — with 29% of unclassified roads in RED condition, up from 17% in 2020, and 206,900 potholes filled in five years. See what the DfT rating means for your claim."
        canonical="/councils/kirklees"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Kirklees', url: '/councils/kirklees' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Kirklees spends nearly double its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Kirklees\' own data shows RED A-roads more than doubled since 2022 and 29% of unclassified roads are now in RED condition.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 1,445.6km — 76% of Kirklees\' 1,894.3km carriageway network — and the council states this is "where most defects are reported." At the latest survey, 29% of U-roads were in RED condition, up from 17% in 2020. The unclassified network is surveyed by Coarse Visual Inspection (CVI), not the automated SCANNER system used on A and B/C roads.',
          },
          {
            question: 'Pothole repairs fell after 2021/22 — does that mean the roads improved?',
            answer: 'Not necessarily. Kirklees filled 56,733 potholes in 2021/22 but only 37,641 in 2024/25 — yet RED U-roads rose from 17% to 29% in the same period. The council\'s own explanation: "The decrease in potholes repaired reflects a drop in reported defects, rather than reduced activity." Condition data and pothole counts are telling different stories.',
          },
          {
            question: 'Does Kirklees saying maintenance helps "defend against claims" weaken my case?',
            answer: 'It shows the council is claim-aware, not claim-proof. Kirklees states its asset management approach "helps defend against claims for damages." That is an admission they rely on documented systems under Section 58 — which you can challenge defect-by-defect with prior reports, photos showing defect age, and evidence the pothole met intervention criteria before you hit it.',
          },
          {
            question: 'A-road RED condition more than doubled — what does that mean for main-road claims?',
            answer: 'SCANNER data shows A-road RED condition rose from 2.1% in 2022 to 4.6% in 2024, while green A-roads fell from 80.0% to 71.5%. B/C roads spiked to 6.3% RED in 2023 before easing to 4.8% in 2024 — still above the 2.6% low point. On any classified road, ask whether the council\'s annual SCANNER survey should have flagged deterioration before your incident.',
          },
          {
            question: 'Why is Kirklees AMBER on best practice as well as condition?',
            answer: 'Unlike councils that earn GREEN across spend and best practice, Kirklees is AMBER on both condition and best practice while GREEN on spend. That means the DfT sees investment without fully matching process standards — relevant because Kirklees will still argue a structured Section 58 defence, but with more gaps than a double-GREEN authority like Leeds.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Kirklees Council"
        councilSlug="kirklees"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/kirklees"
        headline="Kirklees Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Kirklees</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Kirklees: 29% of U-Roads in RED — Nearly Double Since 2020
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Kirklees Council outspends its DfT capital allocation every year and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">29% of unclassified roads are now in RED condition</strong>, up from
            17% in 2020, A-road deterioration has more than doubled since 2022, and the council filled{' '}
            <strong className="text-rose-400">206,900 potholes in five years</strong> on a network where
            three-quarters of carriageway is residential.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">29%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 17% in 2020 — roughly 419km of Kirklees&apos; 1,445.6km unclassified network, where the
              council says most defects are reported.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Kirklees&apos; own transparency report — classified
            roads spiking in 2023, U-roads steadily worsening
          </p>

          {/* Network size */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">The network (1,894.3km total carriageway)</h3>
            <div className="grid sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">210.1km</div>
                <div className="text-sm text-surface-600">A-roads (11.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">238.6km</div>
                <div className="text-sm text-surface-600">B and C roads (12.6%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-rose-600">1,445.6km</div>
                <div className="text-sm text-rose-800">U-roads (76.3%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">1,894.3km</div>
                <div className="text-sm text-surface-600">Total roads</div>
              </div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (210.1km): deteriorating since 2022</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">20.9%</td>
                    <td className="px-4 py-2 text-center">76.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">—</td>
                    <td className="px-4 py-2 text-center">79.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center">17.9%</td>
                    <td className="px-4 py-2 text-center">80.0%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.9%</td>
                    <td className="px-4 py-2 text-center">24.4%</td>
                    <td className="px-4 py-2 text-center">70.7%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.6%</td>
                    <td className="px-4 py-2 text-center">23.9%</td>
                    <td className="px-4 py-2 text-center">71.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition more than doubled from its 2022 low (2.1% → 4.6%), and good-condition
              A-roads fell from 80.0% to 71.5%. Data collected annually via SCANNER survey.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (238.6km): 2023 spike, still elevated</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">23.1%</td>
                    <td className="px-4 py-2 text-center">73.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">23.2%</td>
                    <td className="px-4 py-2 text-center">74.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">23.0%</td>
                    <td className="px-4 py-2 text-center">74.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.3%</td>
                    <td className="px-4 py-2 text-center">29.3%</td>
                    <td className="px-4 py-2 text-center">64.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.8%</td>
                    <td className="px-4 py-2 text-center">26.7%</td>
                    <td className="px-4 py-2 text-center">68.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C RED roads hit 6.3% in 2023 — more than double the 2021/22 figure — before easing to 4.8%
              in 2024. Green B/C roads remain 6 percentage points below their 2022 peak.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£13.1m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£17.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">71%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share (2025/26)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              In 2024/25 Kirklees spent <span className="text-emerald-400">£19.4m</span> against a DfT
              allocation of <span className="text-emerald-400">£10.1m</span> — nearly double. Roads are still
              deteriorating on the metrics that decide claims. The problem isn&apos;t the chequebook alone;
              condition and best practice both sit at AMBER.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,445.6km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            76% of Kirklees carriageway is unclassified — surveyed by CVI, not SCANNER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">17%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">29%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">CVI, Not SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                Kirklees surveys its unclassified network using Coarse Visual Inspection (CVI) — a visual
                assessment — while A and B/C roads get automated SCANNER surveys at traffic speed. The
                transparency report publishes only RED percentages for U-roads, not full amber/green splits.
              </p>
              <p className="text-surface-600 text-sm">
                From 2026/27 a new PAS 2161 methodology will replace the current three-category system with
                five categories — another potential break in comparability for claims spanning years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Where Most Defects Are Reported</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council&apos;s LBUR programme (2021–2024) targeted unclassified roads — yet RED U-roads
                still rose from 17% to 29%. At 29% RED, roughly <strong>419km</strong> of residential and
                estate routes should be considered for maintenance under the council&apos;s own definitions.
              </p>
              <p className="text-amber-800 text-sm">
                Kirklees defines a pothole for reporting purposes as up to a 1m² area. Larger defects may
                be counted differently — worth checking against the specific hole that damaged your vehicle.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Kirklees unclassified network is surveyed using a Coarse Visual Inspection (CVI) survey. It's a visual inspection and focusses on condition areas similar to a SCANNER survey."
              source="Kirklees Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Additionally, the Locality Based Unclassified Roads (LBUR) Programme, funded by Kirklees Council for the years 2021 to 2024, positively impacted the condition of unclassified roads (U-roads), where most defects are reported."
              source="Kirklees Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence under the Highways Act 1980, a council must show it had a
              reasonable system for knowing the condition of its roads. For Kirklees&apos; unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI-surveyed — and what RED band did it fall into?</li>
              <li>• If 29% of U-roads are RED, what proactive works were scheduled for your street?</li>
              <li>• Did Asset Maintenance Technicians inspect your route before the defect formed?</li>
              <li>• Does the CVI methodology catch defects as reliably as SCANNER on classified roads?</li>
            </ul>
            <p className="text-white font-medium">
              A council can&apos;t claim equal knowledge of a network it surveys differently — especially when
              RED U-roads have risen 12 percentage points in five years despite targeted LBUR investment.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            206,900 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair — and the council&apos;s own explanation for falling counts
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">43,775</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">56,733</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">35,212</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">33,539</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">37,641</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">206,900</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The number of potholes Kirklees Highways Service repaired between 2020/21 and 2024/25 has fluctuated, peaking in 2021/22 before gradually declining. This trend aligns with the introduction of a dedicated Asset Maintenance Technicians, who are responsible for highway safety inspections, coordinating reactive maintenance, and delivering early intervention works."
              source="Kirklees Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The decrease in potholes repaired reflects a drop in reported defects, rather than reduced activity. This would indicate fewer potholes are being identified. This highlights the effectiveness of earlier repair efforts and the benefits of targeted investment, leading to more stable and manageable maintenance demands."
              source="Kirklees Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~113 Repairs a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Kirklees fills around 41,380 potholes annually — roughly 113 per day.
                In 2024/25 alone, 37,641 potholes were filled while RED U-roads reached 29%. A network
                producing defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Counting Caveat</h3>
              <p className="text-amber-800 text-sm">
                Kirklees defines a pothole as up to a 1m² area for the purposes of this report. Figures cover
                a financial year. The council attributes falling repair counts to fewer identified defects — not
                less activity — which sits awkwardly alongside rising RED U-road percentages.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Highway Maintenance Spend
          </h2>
          <p className="text-surface-600 mb-8">
            DfT allocation versus actual capital and revenue spend — figures exclude streetlighting energy costs
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£9.8m</td>
                  <td className="px-4 py-3 text-center">£15.6m</td>
                  <td className="px-4 py-3 text-center">£4.0m</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£7.6m</td>
                  <td className="px-4 py-3 text-center">£21.3m</td>
                  <td className="px-4 py-3 text-center">£5.3m</td>
                  <td className="px-4 py-3 text-center">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£10.1m</td>
                  <td className="px-4 py-3 text-center">£21.9m</td>
                  <td className="px-4 py-3 text-center">£5.3m</td>
                  <td className="px-4 py-3 text-center">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£13.3m</td>
                  <td className="px-4 py-3 text-center">£20.1m</td>
                  <td className="px-4 py-3 text-center">£2.7m</td>
                  <td className="px-4 py-3 text-center">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£10.1m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£19.4m</td>
                  <td className="px-4 py-3 text-center">£3.6m</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£13.1m</td>
                  <td className="px-4 py-3 text-center">£17.1m</td>
                  <td className="px-4 py-3 text-center">£3.3m</td>
                  <td className="px-4 py-3 text-center">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">WYMCA and CRSTS Funding</h3>
              <p className="text-surface-600 text-sm mb-3">
                Kirklees receives additional funding via the West Yorkshire Mayoral Combined Authority and
                the City Region Sustainable Transport Settlement (CRSTS), running 2022/23 to 2026/27. Between
                2022/23 and 2024/25, CRSTS delivery included 46.65km of road renewals and 164.15km of
                preventative carriageway treatment.
              </p>
              <p className="text-surface-600 text-sm">
                In 2024/25 the council delivered 62.07km of carriageway preventative maintenance and 34.11km
                of footway preventative maintenance — substantial planned output alongside 37,641 pothole fills.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Forward Plan</h3>
              <p className="text-surface-600 text-sm mb-3">
                The proposed split for 2025/26 is £14,002,386 preventative and £3,138,820 reactive. Pothole
                works are budgeted at £1,916,644 for 2025/26 and £2,250,000 for 2026/27. Surface dressing
                programmes total £1,350,000 in 2025/26.
              </p>
              <p className="text-surface-600 text-sm">
                Major schemes include A6024 Woodhead Road landslip (£820,000 in 2025/26), Bradley Mills Road
                widening (£2,000,000), and A62 Manchester Road Phase 1 resurfacing (£650,000).
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The approach also helps defend against claims for damages, which in turn protects the public purse."
            source="Kirklees Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Inspection and innovation */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspection, AI and the Section 41 Duty
          </h2>
          <p className="text-surface-600 mb-8">
            How Kirklees says it maintains the network — and what that means under Section 41 of the Highways Act 1980
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey Methodology</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A and B/C roads:</strong> SCANNER survey — automated lasers, cameras and sensors at traffic speed, collected annually</li>
                <li>• <strong>U-roads:</strong> Coarse Visual Inspection (CVI) — visual assessment producing RED/amber/green indicators</li>
                <li>• <strong>Safety inspections:</strong> Asset Maintenance Technicians coordinate reactive maintenance and early intervention</li>
                <li>• <strong>AI cameras:</strong> Mobile AI camera technology supports defect identification and forward works programmes</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Inspection Gap Argument</h3>
              <p className="text-amber-800 text-sm mb-3">
                Kirklees states that if inspections &quot;don&apos;t happen regularly, small problems can get worse
                and may eventually lead to serious damage or even failure.&quot; That is the council&apos;s own
                framing of why regular checks matter — directly relevant to whether they met the Section 41
                duty of maintainable repair.
              </p>
              <p className="text-amber-800 text-sm">
                The council follows the Well Managed Highway Infrastructure Code of Practice with a risk-based,
                life-cycle planning approach. Best practice is AMBER, not GREEN — leaving identifiable process
                gaps you can probe in a claim response.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="If these inspections don't happen regularly, small problems can get worse and may eventually lead to serious damage or even failure."
            source="Kirklees Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Kirklees invests heavily — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeds DfT allocation every year in the table
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 71–83% preventative spend across the five-year period
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Well Managed Highway Infrastructure Code of Practice alignment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AI-assisted defect identification and documented asset management systems
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> LBUR programme and CRSTS investment on unclassified roads
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence. The council explicitly states its approach helps defend claims.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — RED U-roads up from 17% to 29% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — weaker process scorecard than double-GREEN neighbours
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 76% of network on CVI survey, not automated SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 206,900 potholes filled in five years — defects outpace planned works
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road RED more than doubled since 2022; B/C spiked to 6.3% RED in 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits falling pothole counts mean fewer defects identified — not better roads
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and claim-aware asset management, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the CVI survey gap and 29% RED network rate are your strongest structural arguments</li>
              <li>• Whether the pothole exceeded the council&apos;s 1m² reporting definition</li>
              <li>• Timing relative to LBUR works or planned 2025/26 resurfacing on your route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Kirklees&apos; own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Kirklees?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No CVI-vs-SCANNER survey argument</li>
                <li>• No prior-report search</li>
                <li>• No U-road deterioration data cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 29% RED U-road trend documented</li>
                <li>✅ CVI survey methodology argued</li>
                <li>✅ 206,900 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Kirklees</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=kirklees"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Kirklees Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Kirklees spends nearly double its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road
                condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific
                defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend.
                Kirklees&apos; own data shows RED A-roads more than doubled since 2022 and 29% of unclassified
                roads are now in RED condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,445.6km — 76% of Kirklees&apos; 1,894.3km carriageway network — and the
                council states this is &quot;where most defects are reported.&quot; At the latest survey, 29% of
                U-roads were in RED condition, up from 17% in 2020. The unclassified network is surveyed by
                Coarse Visual Inspection (CVI), not the automated SCANNER system used on A and B/C roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell after 2021/22 — does that mean the roads improved?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. Kirklees filled 56,733 potholes in 2021/22 but only 37,641 in 2024/25 — yet
                RED U-roads rose from 17% to 29% in the same period. The council&apos;s own explanation:
                &quot;The decrease in potholes repaired reflects a drop in reported defects, rather than reduced
                activity.&quot; Condition data and pothole counts are telling different stories.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Kirklees saying maintenance helps &quot;defend against claims&quot; weaken my case?
              </h3>
              <p className="text-surface-600 text-sm">
                It shows the council is claim-aware, not claim-proof. Kirklees states its asset management
                approach &quot;helps defend against claims for damages.&quot; That is an admission they rely on
                documented systems under Section 58 — which you can challenge defect-by-defect with prior reports,
                photos showing defect age, and evidence the pothole met intervention criteria before you hit it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                A-road RED condition more than doubled — what does that mean for main-road claims?
              </h3>
              <p className="text-surface-600 text-sm">
                SCANNER data shows A-road RED condition rose from 2.1% in 2022 to 4.6% in 2024, while green
                A-roads fell from 80.0% to 71.5%. B/C roads spiked to 6.3% RED in 2023 before easing to 4.8%
                in 2024 — still above the 2.6% low point. On any classified road, ask whether the council&apos;s
                annual SCANNER survey should have flagged deterioration before your incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Kirklees AMBER on best practice as well as condition?
              </h3>
              <p className="text-surface-600 text-sm">
                Unlike councils that earn GREEN across spend and best practice, Kirklees is AMBER on both
                condition and best practice while GREEN on spend. That means the DfT sees investment without
                fully matching process standards — relevant because Kirklees will still argue a structured
                Section 58 defence, but with more gaps than a double-GREEN authority like Leeds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.kirklees.gov.uk/beta/delivering-services/pdf/local-highways-maintenance-transparency-report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Kirklees Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
